import { Component, DestroyRef, inject } from '@angular/core';
import { FormMode } from '../../../shared/types/common';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ManagementService } from '../management.service';
import { StateService } from '../../../services/state.service';
import { CustomValidators } from '../../../shared/validators';
import { objectArrayToSelectOptions } from '../../../utils/mappings';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BrandPayload, BrandSummary } from '../types';
import { TitledInputComponent } from '../../../shared/components/titled-input/titled-input.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { sanitizeAndJoin } from '../../../utils/strings';
import { UtilService } from '../../../services/util.service';
import { SearchBarComponent } from '../../../shared/components/search-bar/search-bar.component';
import { SearchResultListComponent } from '../../../shared/components/search-result-list/search-result-list.component';
import { FormChipListComponent } from '../../../shared/components/form-chip-list/form-chip-list.component';
import { ResultData } from '../../../shared/components/search-result-list/search-result-list.component.types';
import { TooltipIconComponent } from '../../../shared/components/tooltip-icon/tooltip-icon.component';
import { SelectItem, SelectMenuComponent, SelectMode } from '../../../shared/components/select-menu/select-menu.component';
import { tooltips } from '../../../utils/tooltips';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { CommonModule } from '@angular/common';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import { DashboardService } from '../../../services/dashboard.service';
import { DeleteBtnComponent } from '../../../shared/components/delete-btn/delete-btn.component';

export type MetaPageFormGroup = FormGroup<{
  pageId: FormControl<string | null>;
  country: FormControl<string | null>;
}>;

@Component({
  selector: 'app-brand-modal',
  imports: [
    ReactiveFormsModule,
    TitledInputComponent,
    ButtonComponent,
    SearchBarComponent,
    SearchResultListComponent,
    FormChipListComponent,
    TooltipIconComponent,
    SelectMenuComponent,
    LoaderComponent,
    CommonModule,
    AssetVersionPipe,
    DeleteBtnComponent
  ],
  templateUrl: './brand-modal.component.html',
  styleUrl: './brand-modal.component.scss'
})
export class BrandModalComponent {

  public currentMode: FormMode = FormMode.ADD;
  form: FormGroup<{
    brandKey: FormControl<string | null>;
    name: FormControl<string | null>;
    keywords: FormControl<string | null>;
    advertiserDomains: FormControl<ResultData[] | null>;
    manualAdvertiserDomain: FormControl<string | null>;
    logoPath: FormControl<string | null>;
    competingBrands: FormControl<string[] | null>;
    aiPresenceTopics: FormControl<string[] | null>;
    metaPages: FormArray<MetaPageFormGroup>;
  }>;
  public configs = {
    [FormMode.ADD]: {
      title: 'Add brand',
      submitText: 'Add',
      submitFunction: () => this.addBrand()
    },
    [FormMode.EDIT]: {
      title: 'Edit brand',
      submitText: 'Update',
      submitFunction: () => this.editBrand()
    }
  };
  public manualDomains: ResultData[] = [];
  public domainResults: ResultData[] = [];
  public competitorOptions: SelectItem[] = [];
  public topicOptions: SelectItem[] = [];
  public data = inject(MAT_DIALOG_DATA);
  public isSearching = false;
  public resultPlaceholder = ['Search for advertiser domains to add them to the list.', 'Search result will be limited to 1000 domains.']
  public searchControl = new FormControl('');
  public redirectCallback = (result: ResultData) => {
    window.open(`https://${result.value}`, '_blank', 'noopener');
  }
  public logoLoading = false;
  public logoMessageState: { class?: string; message?: string } = { class: undefined, message: undefined };
  public isMetaExpanded = true;
  public countryOptions: SelectItem[] = [];

  protected readonly SelectMode = SelectMode;
  protected readonly tooltips = tooltips.management.brandModal;

  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private dashboardService = inject(DashboardService);
  private managementService = inject(ManagementService);
  private stateService = inject(StateService);
  private utilService = inject(UtilService);
  private lastSearch: string | null | undefined;

  constructor(private dialogRef: MatDialogRef<BrandModalComponent>) {
    this.form = this.buildForm();
    if (this.data) {
      this.initEditMode();
    }
    this.initOptions();
    // Add a default meta page row
    this.metaPages.push(this.createEmptyMetaRow());
  }

  private buildForm() {
    return this.fb.group({
      brandKey: new FormControl({
        value: '',
        disabled: !!this.data
      }, [CustomValidators.required('Brand key'), CustomValidators.brandKey('Brand key')]),
      name: new FormControl('', [CustomValidators.required('Name')]),
      keywords: new FormControl('', [CustomValidators.required('Keywords')]),
      advertiserDomains: new FormControl([] as ResultData[], [CustomValidators.minMaxElements(1)]),
      manualAdvertiserDomain: new FormControl('', [CustomValidators.url('Advertiser domain')]),
      logoPath: new FormControl(''),
      competingBrands: new FormControl<string[]>([]),
      aiPresenceTopics: new FormControl<string[]>([]),
      metaPages: this.fb.array<MetaPageFormGroup>([]),
    });
  }

  private initEditMode(): void {
    this.currentMode = FormMode.EDIT;
    this.form.patchValue({
      ...this.data,
      keywords: sanitizeAndJoin(this.data.keywords),
      advertiserDomains: [...this.selectedResults],
      competingBrands: this.data.competingBrands.map((brand: BrandSummary) => brand.brandKey),
      aiPresenceTopics: this.data.aiTopics || []
    });

    this.domainResults = this.data.advertiserDomains.map((domainString: string) => ({ value: domainString, isSelected: true }));

    this.data.metaPages?.forEach(({ country, page_id }: { country: string; page_id: string }) => {
      const row = this.createEmptyMetaRow();
      row.patchValue({ pageId: page_id, country });
      this.metaPages.push(row);
    });
  }

  private initOptions(): void {
    this.managementService.brandsList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((brands) => {
        this.competitorOptions = objectArrayToSelectOptions(brands, "brandKey", "name").filter(brandKey => brandKey.key !== this.brandKey.value);
      });
    this.topicOptions = this.managementService.aiTopics.map(topic => ({ label: topic.name, key: topic.name }));
    this.countryOptions = this.dashboardService.countriesOptions();
  }

  get brandKey(): FormControl {
    return this.form.get('brandKey') as FormControl;
  }

  get name(): FormControl {
    return this.form.get('name') as FormControl;
  }

  get keywords(): FormControl {
    return this.form.get('keywords') as FormControl;
  }

  get advertiserDomains(): FormControl {
    return this.form.get('advertiserDomains') as FormControl;
  }

  get manualAdvertiserDomain(): FormControl {
    return this.form.get('manualAdvertiserDomain') as FormControl;
  }

  get logoPath(): FormControl {
    return this.form.get('logoPath') as FormControl;
  }

  get competingBrands(): FormControl<string[]> {
    return this.form.get('competingBrands') as FormControl<string[]>;
  }

  get aiPresenceTopics(): FormControl<string[]> {
    return this.form.get('aiPresenceTopics') as FormControl<string[]>;
  }

  get unselectedResults(): ResultData[] {
    return this.domainResults.filter(result => !result.isSelected);
  }

  get selectedResults(): ResultData[] {
    return [...this.domainResults, ...this.manualDomains].filter(result => result.isSelected);
  }

  get metaPages(): FormArray<MetaPageFormGroup> {
    return this.form.get('metaPages') as FormArray<MetaPageFormGroup>;
  }

  submit() {
    this.stateService.loading = true;
    this.configs[this.currentMode].submitFunction();
  }

  cancel() {
    this.dialogRef.close();
  }

  addBrand() {
    this.managementService.addBrand(this.getBrandPayload()).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: result => {
            this.managementService.fetchAndStoreBrands();
            this.dialogRef.close(result)
          },
          error: error => {
            this.stateService.loading = false;
            this.utilService.openSnackBar(error.error.message, { variant: 'error', position: 'center' }
            )
          }
        }
      )
  }

  editBrand() {
    this.managementService.editBrand(this.getBrandPayload()).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: result => {
            this.dialogRef.close(result)
          },
          error: error => {
            this.stateService.loading = false;
            this.utilService.openSnackBar(error.error.message, { variant: 'error', position: 'center' })
          }
        }
      );
  }

  search() {
    this.isSearching = true;
    this.lastSearch = this.searchControl.value;
    if (this.searchControl.value)
      this.managementService.searchAdvertisersDomains(this.searchControl.value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
        const domains = result.domains.map(domain => {
          return {
            value: domain,
            isSelected: false
          }
        })

        this.domainResults = this.selectedResults;
        const filteredDomains = domains.filter(dom1 =>
          !this.domainResults.some(dom2 => dom2.value === dom1.value)
        );
        if (filteredDomains.length === 0) {
          this.resultPlaceholder = ['No domains found 🥺', 'But here’s a unicorn: 🦄'];
        } else {
          this.domainResults.push(...filteredDomains);
        }
        this.isSearching = false;
      });

  }

  addManualAdvertiserDomain(): void {
    if (this.manualAdvertiserDomain.invalid) {
      return;
    }
    const domain = this.manualAdvertiserDomain.value.trim();
    if (domain) {
      this.manualDomains.push({ value: domain, isSelected: true });
      this.manualAdvertiserDomain.reset();
    }
  }

  copyAdvertiserDomains() {
    const snowflakeList = `(${this.selectedResults.map(result => {
      return `'${result.value}'`
    }).join(',')})`;
    navigator.clipboard.writeText(snowflakeList);
  }

  getValidSearch(): boolean {
    const searchVal = this.searchControl.value as string;
    return searchVal.length > 2 && !this.isSearching && searchVal !== this.lastSearch;
  }

  getBrandPayload() {
    const form = this.form.getRawValue();
    const metaPages = this.metaPages.controls
      .filter(row => row.get('pageId')?.value && row.get('country')?.value)
      .map(row => ({
        country: row.get('country')!.value as string,
        page_id: row.get('pageId')!.value as string,
      }));
    return {
      brandKey: form.brandKey,
      name: form.name,
      competingBrands: form.competingBrands,
      keywords: this.keywords.value.split(','),
      advertiserDomains: this.selectedResults.map(domain => domain.value),
      logoPath: this.logoPath.value || undefined,
      aiPresenceTopics: form.aiPresenceTopics,
      metaPages
    } as BrandPayload;
  }

  onLogoError(event: Event): void {
    (event.target as HTMLImageElement).src = 'images/no-logo.png';
  }

  findLogo(): void {
    const brandName = this.name.value;
    if (!brandName || this.logoLoading) {
      return;
    }
    this.logoLoading = true;
    this.managementService.findBrandLogo(brandName).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: result => {
            if (result?.logoURL) {
              this.logoPath.setValue(result.logoURL);
              this.logoMessageState = { class: 'success', message: 'Logo found. If incorrect, upload a custom one.' };
            } else {
              this.logoMessageState = { class: 'error', message: 'We couldn’t find a logo for this brand. Change name or upload a logo.' };
            }
            this.logoLoading = false;
          },
          error: error => {
            this.logoMessageState = { class: 'error', message: 'An error occurred. Please try again.' };
            this.logoLoading = false;
          }
        }
      )
  }

  uploadLogo(): void {
    const brandName = this.name.value;
    if (!brandName || this.logoLoading) {
      return;
    }
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg, image/png';

    fileInput.onchange = () => {
      const files = fileInput.files;
      if (files && files.length > 0) {
        const logoFile = files[0];
        if (!this.isValidLogoFile(logoFile)) {
          return;
        }
        this.handleLogoUpload(brandName, logoFile);
      }
    };
    fileInput.click();
  }

  private isValidLogoFile(file: File): boolean {
    const maxSizeInMB = 2;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      this.logoMessageState = { class: 'error', message: `File size exceeds the maximum limit of ${maxSizeInMB} MB` };
      return false;
    }

    return true;
  }

  private handleLogoUpload(brandName: string, logoFile: File): void {
    this.logoLoading = true;
    this.managementService.uploadBrandLogo(brandName, logoFile).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: result => {
            if (result?.logoURL) {
              this.logoPath.setValue(result.logoURL);
              this.logoMessageState = { class: 'success', message: 'Logo uploaded successfully.' };
            } else {
              this.logoMessageState = { class: 'error', message: 'An error occurred. Please try again.' }
            }
            this.logoLoading = false;
          },
          error: error => {
            if (error.status === 400) {
              this.logoMessageState = { class: 'error', message: error.error.message };
            } else {
              this.logoMessageState = { class: 'error', message: 'An error occurred. Please try again.' }
            }
            this.logoLoading = false;
          }
        }
      );
  }

  private createEmptyMetaRow(): MetaPageFormGroup {
    return this.fb.group({
      pageId: new FormControl<string | null>(null, [CustomValidators.numericOnly('Meta page ID')]),
      country: new FormControl<string | null>(null),
    }) as MetaPageFormGroup;
  }

  getMetaPageIdControl(index: number): FormControl<string | null> {
    return this.metaPages.at(index).get('pageId') as FormControl<string | null>;
  }

  getMetaCountryControl(index: number): FormControl<string | null> {
    return this.metaPages.at(index).get('country') as FormControl<string | null>;
  }

  isMetaRowEmpty(index: number): boolean {
    const row = this.metaPages.at(index);
    return !row.get('pageId')?.value && !row.get('country')?.value;
  }

  toggleMeta(): void {
    this.isMetaExpanded = !this.isMetaExpanded;
  }

  removeMetaRow(index: number): void {
    this.metaPages.removeAt(index);
  }

  checkAndAddMetaRow(index: number): void {
    if (index === this.metaPages.length - 1) {
      const row = this.metaPages.at(index);
      if (row.get('pageId')?.value && row.get('country')?.value) {
        this.metaPages.push(this.createEmptyMetaRow());
      }
    }
  }

}
